import { ChevronLeft,ChevronRight,Info,Share2 } from 'lucide-react';
import { useEffect,useRef,useState } from 'react';
import { useLocation } from 'react-router-dom';
import siteRoutes from '../../lib/siteRoutes.json';
import { useWidgetStore } from '../../lib/store';
import InfoModal from './InfoModal';

export default function Widget() {
  const { isExpanded, toggleExpanded, toggleInfo, showInfo, isVisible, toggleVisibility } = useWidgetStore();
  const { pathname } = useLocation();
  const page = siteRoutes.find(route => route.path === pathname);
  const [isMobile, setIsMobile] = useState(false);
  const [top, setTop] = useState<number | null>(null);
  const toolbar = useRef<HTMLElement>(null);
  const drag = useRef<{ y: number; top: number } | null>(null);
  const wasDragged = useRef(false);
  const infoButton = useRef<HTMLButtonElement>(null);
  const [shareMessage, setShareMessage] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const clamp = (value: number) => Math.max(88, Math.min(value, window.innerHeight - (toolbar.current?.offsetHeight ?? 128) - 16));

  useEffect(() => {
    const resize = () => {
      setIsMobile(window.innerWidth <= 768);
      setTop(value => value === null ? null : Math.max(88, Math.min(value, window.innerHeight - (toolbar.current?.offsetHeight ?? 128) - 16)));
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  useEffect(() => {
    setShareMessage('');
    setShareUrl('');
    toggleInfo(false);
  }, [pathname, toggleInfo]);

  const closeInfo = () => { toggleInfo(false); infoButton.current?.focus(); };
  const shareCalculator = async () => {
    if (wasDragged.current) return;
    const url = new URL(pathname, window.location.origin).href;
    setShareUrl(url);
    setShareMessage('Copy this calculator link:');
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ title: page?.heading || 'Calculato calculator', url });
        setShareMessage('Shared.');
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setShareMessage('Calculator link copied.');
      } else { setShareMessage('Copy this calculator link:'); setShareUrl(url); }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      setShareMessage('Copy this calculator link:'); setShareUrl(url);
    }
  };

  if (!page || !pathname.endsWith('-calculator') || page.indexable === false) return null;
  const buttonClass = 'flex h-8 w-8 items-center justify-center rounded-full text-white hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white';
  return <>
    {shareMessage && <div role="status" className="fixed bottom-4 right-4 z-[70] w-72 max-w-[calc(100vw-32px)] rounded-xl bg-gray-900 p-4 text-white shadow-lg">
      <p>{shareMessage}</p>
      {shareUrl && <input aria-label="Calculator link" readOnly value={shareUrl} onFocus={event => event.currentTarget.select()} className="mt-2 w-full rounded p-2 text-gray-900" />}
      <button onClick={() => { setShareMessage(''); setShareUrl(''); }} className="mt-2 underline">Dismiss</button>
    </div>}
    <InfoModal calculatorId={pathname} isOpen={showInfo} onClose={closeInfo} onDismiss={() => toggleInfo(false)} isMobile={isMobile} />
    {!isMobile && !isVisible && <button onClick={() => toggleVisibility(true)} style={{ top: top ?? 'calc(50dvh - 64px)' }} className="fixed right-0 z-50 rounded-l-xl bg-violet-600 p-3 text-white shadow-lg" aria-label="Show calculator widget"><Info size={20} /></button>}
    {(isVisible || isMobile) && <aside ref={toolbar} aria-label="Calculator tools" className={`fixed right-1 z-50 bg-gradient-to-b from-violet-600 to-fuchsia-600 text-white shadow-lg ${isMobile ? 'flex h-12 items-center rounded-l-full -right-1 px-1' : 'top-[calc(50dvh-64px)] right-4 flex flex-col gap-2 rounded-2xl p-2'}`} style={{ top: top ?? (isMobile ? 'calc(100dvh - 112px)' : 'calc(50dvh - 64px)') }}>
      {isMobile && <button className="flex h-8 w-5 touch-none items-center justify-center text-white" title="Drag up or down to move" aria-label={isExpanded ? 'Collapse calculator widget' : 'Expand calculator widget'} onPointerDown={event => {
        wasDragged.current = false;
        drag.current = { y: event.clientY, top: event.currentTarget.parentElement!.getBoundingClientRect().top };
        event.currentTarget.setPointerCapture(event.pointerId);
      }} onPointerMove={event => { if (drag.current && Math.abs(event.clientY - drag.current.y) > 6) { wasDragged.current = true; setTop(clamp(drag.current.top + event.clientY - drag.current.y)); } }} onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; wasDragged.current = true; }} onClick={() => { if (!wasDragged.current) { toggleExpanded(); toggleInfo(false); } }} onKeyDown={event => {
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') { event.preventDefault(); setTop(clamp((top ?? window.innerHeight - 112) + (event.key === 'ArrowUp' ? -48 : 48))); }
      }}>{isExpanded ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}</button>}
      {(!isMobile || isExpanded) && <>
        <button ref={infoButton} className={buttonClass} aria-label={`About ${page.heading}`} aria-expanded={showInfo} aria-controls="calculator-information" onPointerDown={() => { wasDragged.current = false; }} onClick={() => { if (!wasDragged.current) toggleInfo(); }}><Info size={20} /></button>
        <button className={buttonClass} aria-label="Share calculator" onPointerDown={() => { wasDragged.current = false; }} onClick={shareCalculator}><Share2 size={20} /></button>
      </>}
      {!isMobile && <button className={buttonClass + ' touch-none cursor-grab'} title="Drag up or down to move; click to hide" aria-label="Hide calculator widget" onPointerDown={event => {
        wasDragged.current = false;
        drag.current = { y: event.clientY, top: event.currentTarget.parentElement!.getBoundingClientRect().top };
        event.currentTarget.setPointerCapture(event.pointerId);
      }} onPointerMove={event => { if (drag.current && Math.abs(event.clientY - drag.current.y) > 6) { wasDragged.current = true; setTop(clamp(drag.current.top + event.clientY - drag.current.y)); } }} onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; wasDragged.current = true; }} onKeyDown={event => {
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') { event.preventDefault(); setTop(clamp((top ?? window.innerHeight / 2 - 64) + (event.key === 'ArrowUp' ? -48 : 48))); }
      }} onClick={() => { if (!wasDragged.current) { toggleVisibility(false); toggleInfo(false); } }}><ChevronRight size={20} /></button>}
    </aside>}
  </>;
}
